import React, { useRef, useCallback, useState, useMemo } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import Live2DStage from './live2d/Live2DStage';
import { buildLive2dModelUrlFromParts } from '../utils/live2dUrls';

const LIVE2D_PUBLIC = process.env.PUBLIC_URL || '';

const LAYERS = [
  { id: 'background', label: 'Background' },
  { id: 'character', label: 'Character' },
  { id: 'foreground', label: 'Foreground' }
];

/**
 * 16:9 canvas: the layer selected in the toggle is moved by drag anywhere on the canvas (full hit overlay).
 * Scale is controlled by sliders (parent). Rect coordinates are normalized 0–1 vs frame.
 */
export default function SceneCanvasEditor({
  backgroundRect,
  setBackgroundRect,
  characterRect,
  setCharacterRect,
  foregroundRect,
  setForegroundRect,
  bgThumb,
  fgThumb,
  charDoc,
  hasBackground,
  hasCharacter,
  hasForeground,
  maxWidth = 720
}) {
  const rootRef = useRef(null);
  const dragRef = useRef(null);
  const [activeLayer, setActiveLayer] = useState('background');

  const live2dModelUrl = useMemo(
    () => buildLive2dModelUrlFromParts(LIVE2D_PUBLIC, charDoc?.live2dPack, charDoc?.live2dModelPath),
    [charDoc?.live2dPack, charDoc?.live2dModelPath]
  );
  const idleMotionIndex = useMemo(() => {
    const n = Number(charDoc?.live2dIdleMotionIndex);
    return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
  }, [charDoc?.live2dIdleMotionIndex]);

  const getRectSetter = useCallback(
    (id) => {
      if (id === 'background') return setBackgroundRect;
      if (id === 'character') return setCharacterRect;
      return setForegroundRect;
    },
    [setBackgroundRect, setCharacterRect, setForegroundRect]
  );

  const getRect = useCallback(
    (id) => {
      if (id === 'background') return backgroundRect;
      if (id === 'character') return characterRect;
      return foregroundRect;
    },
    [backgroundRect, characterRect, foregroundRect]
  );

  const layerEnabled = (id) => {
    if (id === 'background') return hasBackground;
    if (id === 'character') return hasCharacter;
    return hasForeground;
  };

  const endDrag = useCallback(() => {
    dragRef.current = null;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  const attachDragListeners = useCallback(
    (getRectSetterFn) => {
      const onMove = (e) => {
        const drag = dragRef.current;
        const root = rootRef.current;
        if (!drag || !root) return;

        const cr = root.getBoundingClientRect();
        const nx = (e.clientX - cr.left) / cr.width;
        const ny = (e.clientY - cr.top) / cr.height;
        const setRect = getRectSetterFn(drag.layerId);
        const sr = drag.startRect;

        if (drag.kind === 'move') {
          setRect({
            ...sr,
            x: nx - drag.grabNX,
            y: ny - drag.grabNY
          });
        }
      };

      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        endDrag();
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    [endDrag]
  );

  const startMove = (layerId, e, rect) => {
    if (!rootRef.current) return;
    e.stopPropagation();
    e.preventDefault();
    const cr = rootRef.current.getBoundingClientRect();
    const nx = (e.clientX - cr.left) / cr.width;
    const ny = (e.clientY - cr.top) / cr.height;
    dragRef.current = {
      kind: 'move',
      layerId,
      startRect: { ...rect },
      grabNX: nx - rect.x,
      grabNY: ny - rect.y
    };
    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';
    attachDragListeners(getRectSetter);
  };

  const activeOutline = (layerId) =>
    activeLayer === layerId
      ? '2px solid rgba(168, 85, 247, 0.95)'
      : '2px solid transparent';

  const hitZ = (layerId) => {
    if (layerId === 'background') return 1;
    if (layerId === 'character') return 2;
    return 3;
  };

  return (
    <Box sx={{ width: '100%', maxWidth }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Canvas 16:9 — choose a layer above, then drag anywhere on the canvas to move it.
        </Typography>
        <ToggleButtonGroup
          size="small"
          value={activeLayer}
          exclusive
          onChange={(_, v) => v && layerEnabled(v) && setActiveLayer(v)}
        >
          {LAYERS.map(({ id, label }) => (
            <ToggleButton key={id} value={id} disabled={!layerEnabled(id)} sx={{ py: 0.25, px: 1, fontSize: 11 }}>
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Box
        ref={rootRef}
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '16 / 9',
          bgcolor: 'grey.900',
          borderRadius: 1,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          touchAction: 'none'
        }}
      >
        {/* Background */}
        <Box
          sx={{
            position: 'absolute',
            left: `${backgroundRect.x * 100}%`,
            top: `${backgroundRect.y * 100}%`,
            width: `${backgroundRect.width * 100}%`,
            height: `${backgroundRect.height * 100}%`,
            zIndex: hitZ('background'),
            outline: activeOutline('background'),
            outlineOffset: -1,
            boxSizing: 'border-box',
            pointerEvents: 'none'
          }}
        >
          {bgThumb ? (
            <Box
              component="img"
              src={bgThumb}
              alt=""
              draggable={false}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                objectPosition: 'center',
                pointerEvents: 'none',
                display: 'block'
              }}
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                bgcolor: 'grey.800',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                typography: 'caption',
                color: 'grey.500',
                pointerEvents: 'none'
              }}
            >
              No background asset
            </Box>
          )}
        </Box>

        {/* Character */}
        {hasCharacter ? (
          <Box
            sx={{
              position: 'absolute',
              left: `${characterRect.x * 100}%`,
              top: `${characterRect.y * 100}%`,
              width: `${characterRect.width * 100}%`,
              height: `${characterRect.height * 100}%`,
              zIndex: hitZ('character'),
              outline: activeOutline('character'),
              outlineOffset: -1,
              boxSizing: 'border-box',
              bgcolor: 'transparent',
              overflow: 'hidden',
              pointerEvents: 'none'
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                minWidth: 0,
                pointerEvents: 'none'
              }}
            >
              {live2dModelUrl ? (
                <Live2DStage
                  key={`${live2dModelUrl}-${idleMotionIndex}`}
                  modelUrl={live2dModelUrl}
                  motionGroup="idle"
                  motionIndex={idleMotionIndex}
                  backgroundColor={0}
                  backgroundAlpha={0}
                  layoutVerticalAlign="bottom"
                  layoutHorizontalRatio={0.5}
                  layoutScaleMultiplier={1}
                  className="min-h-0 min-w-0 flex-1"
                />
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 0.5,
                    bgcolor: 'rgba(156, 39, 176, 0.1)'
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', lineHeight: 1.35 }}>
                    No Live2D on this character. Set Live2D pack and model path in the character editor
                    {charDoc?.name ? ` (${charDoc.name})` : ''}.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        ) : null}

        {/* Foreground */}
        {hasForeground ? (
          <Box
            sx={{
              position: 'absolute',
              left: `${foregroundRect.x * 100}%`,
              top: `${foregroundRect.y * 100}%`,
              width: `${foregroundRect.width * 100}%`,
              height: `${foregroundRect.height * 100}%`,
              zIndex: hitZ('foreground'),
              outline: activeOutline('foreground'),
              outlineOffset: -1,
              boxSizing: 'border-box',
              pointerEvents: 'none'
            }}
          >
            {fgThumb ? (
              <Box
                component="img"
                src={fgThumb}
                alt=""
                draggable={false}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  objectPosition: 'center',
                  pointerEvents: 'none',
                  display: 'block'
                }}
              />
            ) : null}
          </Box>
        ) : null}

        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 20,
            cursor: layerEnabled(activeLayer) ? 'grab' : 'default',
            touchAction: 'none'
          }}
          onPointerDown={(e) => {
            if (!layerEnabled(activeLayer)) return;
            startMove(activeLayer, e, getRect(activeLayer));
          }}
        />
      </Box>
    </Box>
  );
}
